import {
  FlowBlockValueTemplateHint,
  FlowDynamicValueType,
  validateFlowBlockValueTemplate,
} from 'flow-schema';
import { z } from 'zod';

export const DynamicValueSchema = z
  .discriminatedUnion('type', [
    z.object({
      type: z.undefined(),
    }),
    z.object({
      type: z.literal(FlowDynamicValueType.Auto),
      jsonOrTemplate: z.string().optional(),
    }),
    z.object({
      type: z.literal(FlowDynamicValueType.Block),
      blockName: z.string().min(1, 'Block is required'),
    }),
    z.object({
      type: z.literal(FlowDynamicValueType.Element),
    }),
    z.object({
      type: z.literal(FlowDynamicValueType.Expression),
      expression: z
        .string()
        .refine(
          (value) => !value.startsWith('{{'),
          'Do not use block templates like {{blockName.data}}. i.e. Remove `{{ }}` '
        ),
    }),
    z.object({
      type: z.literal(FlowDynamicValueType.Literal),
      value: z.unknown(),
      json: z.string().optional(),
    }),
    z.object({
      type: z.literal(FlowDynamicValueType.Template),
      template: z.string(),
    }),
  ])
  .superRefine((value, ctx) => {
    if (value.type === FlowDynamicValueType.Auto) {
      const { jsonOrTemplate } = value;
      if (jsonOrTemplate) {
        if (jsonOrTemplate.startsWith('{{')) {
          const result = validateFlowBlockValueTemplate(jsonOrTemplate);
          if (result.hint === FlowBlockValueTemplateHint.NotTemplateLike) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Must end with }}',
            });
          } else if (result.hint === FlowBlockValueTemplateHint.Invalid) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: result.message,
            });
          }
        } else {
          try {
            JSON.parse(jsonOrTemplate);
          } catch (error) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `Invalid JSON: ${error instanceof Error ? error.message : String(error)}`,
            });
          }
        }
      }
    } else if (value.type === FlowDynamicValueType.Literal) {
      // `json` must be valid JSON and must parse to `value`
      let parsedValue: unknown = undefined;
      try {
        parsedValue = value.json ? JSON.parse(value.json) : undefined;
      } catch (error) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Invalid JSON: ${error instanceof Error ? error.message : String(error)}`,
          path: ['json'],
        });
        return;
      }
      if (JSON.stringify(parsedValue) !== JSON.stringify(value.value)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `"value" does not match parsed value of "json"`,
        });
      }
    }
  });
export type DynamicValue = z.infer<typeof DynamicValueSchema>;
